import {
  BoxGeometry,
  Box3,
  CylinderGeometry,
  Group,
  IcosahedronGeometry,
  Mesh,
  MeshLambertMaterial,
  SphereGeometry,
  type BufferGeometry,
  type Material,
  type Object3D,
} from 'three';

/**
 * JEFE-4 El Chato (GDD §9.2, §4.3, §3.4, issue #134): a parody caricature, never a real name or insignia. A short, vain
 * figure with a cap and a big moustache who pops out of the tunnels of the floor and the ceiling, rides a low-poly
 * motorbike in the second phase and retires into his own cell (a joke about his vanity: cardboard bars, a bed and a
 * photo of himself). Low-poly with flat shading, from primitives, about 2.9 x 2.9 u (the body hitbox of 2.2 u is 75 % of it).
 * No weapon of any kind is drawn: the barrels are barrels and nobody is hurt (GDD §3.4). Every object of the boss
 * carries `userData.tags` and none of them is `weapon`.
 */
export interface Jefe4Model {
  /** The figure: its origin is the centre of the body, so at the mouth of a tunnel it sits half out. */
  root: Group;
  moto: Group;
  trapdoorFloor: Group;
  trapdoorCeiling: Group;
  /** The dust of the warning: a few puffs that swell (a static cloud with reduced motion). */
  dust: Group;
  /** The comic cell of the retreat, in the middle plane. */
  cell: Group;
  /** The arms, for the animations. */
  arms: Group[];
  dispose(): void;
}

export const JEFE_4_COLORS = {
  suit: 0x6a5a3a,
  shirt: 0xe9e2c8,
  skin: 0xd9a37a,
  cap: 0x2f4a6a,
  brim: 0x24384f,
  moustache: 0x2a2018,
  fist: 0xd9a37a,
  trapdoor: 0x5a4a3a,
  trapdoorEdge: 0x3a2f24,
  dust: 0xc9b48a,
  moto: 0x8a3a2a,
  wheel: 0x2b2b2b,
  seat: 0x3a3a3a,
  bars: 0xb8a888,
  bed: 0x7a8aa8,
  photo: 0xe9e2c8,
  frame: 0x6b4a32,
  wall: 0x8a8070,
} as const;

/** Boss tags (the sensitivity test of GDD §3.4): none of them is `weapon`. */
export const JEFE_4_TAGS = ['boss', 'parody', 'jefe4'] as const;

/** Puffs of the dust cloud of the warning. */
export const DUST_PUFFS = 7;

export function buildJefe4(): Jefe4Model {
  const geometries: BufferGeometry[] = [];
  const materials: Material[] = [];
  const paint = (color: number) => {
    const material = new MeshLambertMaterial({ color, flatShading: true });
    materials.push(material);
    return material;
  };
  const part = <G extends BufferGeometry>(geometry: G, color: number, at: [number, number, number], parent?: Object3D) => {
    geometries.push(geometry);
    const mesh = new Mesh(geometry, paint(color));
    mesh.position.set(...at);
    parent?.add(mesh);
    return mesh;
  };
  const tag = (object: Object3D, name: string) => {
    object.name = name;
    object.userData.tags = [...JEFE_4_TAGS];
    return object;
  };

  // The figure: a short wide body in a brown suit, a big head with a moustache and a cap, and two arms held out (he throws
  // barrels with both hands). The whole figure is about 2.9 u wide and tall, centred on the body.
  const root = tag(new Group(), 'boss-jefe4') as Group;
  root.userData.pose = 'hidden';
  part(new BoxGeometry(1.5, 1.7, 0.8), JEFE_4_COLORS.suit, [0, -0.5, 0], root);
  part(new BoxGeometry(0.4, 1.1, 0.82), JEFE_4_COLORS.shirt, [0, -0.35, 0], root);
  part(new SphereGeometry(0.65, 8, 6), JEFE_4_COLORS.skin, [0, 0.75, 0], root);
  part(new BoxGeometry(0.9, 0.16, 0.2), JEFE_4_COLORS.moustache, [0, 0.55, 0.62], root);
  part(new CylinderGeometry(0.62, 0.7, 0.4, 8), JEFE_4_COLORS.cap, [0, 1.38, 0], root);
  part(new BoxGeometry(1.1, 0.08, 0.5), JEFE_4_COLORS.brim, [0, 1.2, 0.55], root);
  const arms: Group[] = [];
  for (const side of [-1, 1]) {
    const arm = new Group();
    arm.position.set(side * 0.75, 0, 0);
    part(new BoxGeometry(0.6, 0.3, 0.3), JEFE_4_COLORS.suit, [side * 0.3, 0, 0], arm);
    part(new SphereGeometry(0.22, 6, 4), JEFE_4_COLORS.fist, [side * 0.5, 0, 0], arm);
    root.add(arm);
    arms.push(arm);
  }

  // The trapdoors of the floor and the ceiling: a lid that opens upwards or downwards.
  const makeTrapdoor = (name: string): Group => {
    const holder = tag(new Group(), name) as Group;
    holder.userData.pose = 'closed';
    part(new BoxGeometry(2.6, 0.12, 1.2), JEFE_4_COLORS.trapdoor, [0, 0, 0], holder);
    part(new BoxGeometry(2.8, 0.06, 1.3), JEFE_4_COLORS.trapdoorEdge, [0, 0.04, 0], holder);
    return holder;
  };
  const trapdoorFloor = makeTrapdoor('trapdoor-floor');
  const trapdoorCeiling = makeTrapdoor('trapdoor-ceiling');

  // The dust cloud: a handful of faceted puffs that swell while the tunnel is about to open.
  const dust = tag(new Group(), 'tunnel-dust') as Group;
  for (let i = 0; i < DUST_PUFFS; i++) part(new IcosahedronGeometry(0.35, 0), JEFE_4_COLORS.dust, [(i - 3) * 0.45, (i % 3) * 0.2, 0], dust);
  dust.visible = false;

  // The motorbike of the second phase: a frame, two wheels and a seat; the figure rides it.
  const moto = tag(new Group(), 'boss-moto') as Group;
  part(new BoxGeometry(1.8, 0.35, 0.5), JEFE_4_COLORS.moto, [0, 0.1, 0], moto);
  part(new BoxGeometry(0.7, 0.12, 0.45), JEFE_4_COLORS.seat, [-0.2, 0.36, 0], moto);
  for (const x of [-0.75, 0.75]) part(new CylinderGeometry(0.4, 0.4, 0.3, 10), JEFE_4_COLORS.wheel, [x, -0.2, 0], moto).rotation.x = Math.PI / 2;
  moto.visible = false;

  // The comic cell of the retreat: a back wall, cardboard bars, a bed and a photo of himself on the wall.
  const cell = tag(new Group(), 'boss-cell') as Group;
  part(new BoxGeometry(4.2, 3.4, 0.2), JEFE_4_COLORS.wall, [0, 0, -0.4], cell);
  for (const x of [-1.8, -0.9, 0, 0.9, 1.8]) part(new BoxGeometry(0.12, 3.4, 0.12), JEFE_4_COLORS.bars, [x, 0, 0.5], cell);
  part(new BoxGeometry(4.2, 0.12, 0.12), JEFE_4_COLORS.bars, [0, 1.6, 0.5], cell);
  part(new BoxGeometry(2.0, 0.3, 0.9), JEFE_4_COLORS.bed, [1.0, -1.45, 0], cell);
  part(new BoxGeometry(0.9, 1.1, 0.08), JEFE_4_COLORS.frame, [-1.2, 0.7, -0.25], cell);
  part(new BoxGeometry(0.7, 0.9, 0.1), JEFE_4_COLORS.photo, [-1.2, 0.7, -0.2], cell);
  cell.visible = false;

  root.visible = false;
  return {
    root,
    moto,
    trapdoorFloor,
    trapdoorCeiling,
    dust,
    cell,
    arms,
    dispose() {
      for (const geometry of geometries) geometry.dispose();
      for (const material of materials) material.dispose();
    },
  };
}

/** The visual size of the figure (u), for the hitbox of the body (70 to 80 %). */
export function visualSize(model: Jefe4Model): { width: number; height: number } {
  const box = new Box3().setFromObject(model.root);
  return { width: box.max.x - box.min.x, height: box.max.y - box.min.y };
}

/** Every object of the boss and its tags, to check that none is a weapon (GDD §3.4). */
export function tagsOf(model: Jefe4Model): string[] {
  const tags: string[] = [];
  for (const object of [model.root, model.moto, model.trapdoorFloor, model.trapdoorCeiling, model.dust, model.cell]) object.traverse((child) => tags.push(...((child.userData.tags as string[] | undefined) ?? [])));
  return tags;
}
